import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  ArrowLeft, 
  Save, 
  DollarSign,
  Calendar,
  FileText,
  AlertCircle,
  Plus,
  Edit3,
  X,
  List
} from 'lucide-react';
import { registrarPlan, obtenerTiposPlanes } from '../../services/centroService';
import { toast } from 'react-toastify';

const CrearPlan = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    tipo: 'matricula',
    nombre: '',
    descripcion: '',
    monto: '',
    hora_inicio: '',
    hora_fin: '',
    estado: true
  });
  const [errors, setErrors] = useState({});
  const [tiposDisponibles, setTiposDisponibles] = useState(['matricula', 'mensualidad', 'curso', 'actividad']);
  const [modoInput, setModoInput] = useState(false); // false = select, true = input
  const [nuevoTipo, setNuevoTipo] = useState('');
  const [beneficios, setBeneficios] = useState(['']); // Array de beneficios
  const [descripcionBase, setDescripcionBase] = useState(''); // Descripción base sin beneficios

  // Cargar tipos de planes al montar el componente
  useEffect(() => {
    const cargarTipos = async () => {
      try {
        const tipos = await obtenerTiposPlanes();
        setTiposDisponibles(tipos);
      } catch (error) {
        console.error('Error al cargar tipos de planes:', error);
      }
    };
    cargarTipos();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const toggleModoInput = () => {
    setModoInput(!modoInput);
    if (!modoInput) {
      // Cambiar a modo input
      setNuevoTipo(formData.tipo);
    } else {
      // Cambiar a modo select
      setFormData(prev => ({ ...prev, tipo: nuevoTipo || 'matricula' }));
    }
  };

  const handleNuevoTipoChange = (e) => {
    const valor = e.target.value;
    setNuevoTipo(valor);
    setFormData(prev => ({ ...prev, tipo: valor }));
  };

  // Funciones para manejar beneficios
  const agregarBeneficio = () => {
    setBeneficios([...beneficios, '']);
  };

  const eliminarBeneficio = (index) => {
    if (beneficios.length > 1) {
      const nuevosBeneficios = beneficios.filter((_, i) => i !== index);
      setBeneficios(nuevosBeneficios);
      actualizarDescripcionCompleta(descripcionBase, nuevosBeneficios);
    }
  };

  const handleBeneficioChange = (index, valor) => {
    const nuevosBeneficios = [...beneficios];
    nuevosBeneficios[index] = valor;
    setBeneficios(nuevosBeneficios);
    actualizarDescripcionCompleta(descripcionBase, nuevosBeneficios);
  };

  const handleDescripcionBaseChange = (e) => {
    const valor = e.target.value;
    setDescripcionBase(valor);
    actualizarDescripcionCompleta(valor, beneficios);
  };

  // Función para actualizar la descripción completa
  const actualizarDescripcionCompleta = (descripcion, listaBeneficios) => {
    const beneficiosFiltrados = listaBeneficios.filter(b => b.trim() !== '');
    let descripcionCompleta = descripcion || '';
    
    if (beneficiosFiltrados.length > 0) {
      // Agregar ; antes de cada beneficio para asegurar separación correcta
      const beneficiosConSeparador = beneficiosFiltrados.map(b => ';' + b).join('');
      descripcionCompleta += beneficiosConSeparador;
    }
    
    setFormData(prev => ({ ...prev, descripcion: descripcionCompleta }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tipo.trim()) {
      newErrors.tipo = 'El tipo de plan es obligatorio';
    }

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre del plan es obligatorio';
    }

    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es obligatoria';
    }

    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      newErrors.monto = 'El precio debe ser mayor a 0';
    }

    if (!formData.hora_inicio.trim()) {
      newErrors.hora_inicio = 'La hora de inicio es obligatoria';
    }

    if (!formData.hora_fin.trim()) {
      newErrors.hora_fin = 'La hora de fin es obligatoria';
    }

    if (formData.hora_inicio && formData.hora_fin && formData.hora_inicio >= formData.hora_fin) {
      newErrors.hora_fin = 'La hora de fin debe ser posterior a la hora de inicio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrige los errores en el formulario');
      return;
    }

    setLoading(true);
    try {
      const planData = {
        ...formData,
        monto: parseFloat(formData.monto)
      };

      const response = await registrarPlan(planData);
      
      if (response.success) {
        toast.success('Plan creado correctamente');
        navigate('/admin/centro/planes');
      } else {
        toast.error(response.message || 'Error al crear el plan');
      }
    } catch (error) {
      console.error('Error al crear plan:', error);
      toast.error('Error al crear el plan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/centro/planes')}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Crear Nuevo Plan</h1>
                <p className="text-gray-600">Completa la información del nuevo plan</p>
              </div>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tipo de plan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Package className="inline h-4 w-4 mr-1" />
                Tipo de Plan *
              </label>
              <div className="flex gap-2">
                {!modoInput ? (
                  <select
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleInputChange}
                    className={`flex-1 px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm ${
                      errors.tipo ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                  >
                    {tiposDisponibles.map(tipo => (
                      <option key={tipo} value={tipo}>
                        {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={nuevoTipo}
                    onChange={handleNuevoTipoChange}
                    placeholder="Escribir nuevo tipo..."
                    className={`flex-1 px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm ${
                      errors.tipo ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                  />
                )}
                <button
                  type="button"
                  onClick={toggleModoInput}
                  className="px-4 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition-colors flex items-center gap-2"
                  title={modoInput ? 'Seleccionar de lista' : 'Escribir nuevo tipo'}
                >
                  {modoInput ? <Package className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </button>
              </div>
              {errors.tipo && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.tipo}
                </p>
              )}
            </div>

            {/* Nombre del plan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Package className="inline h-4 w-4 mr-1" />
                Nombre del Plan *
              </label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm ${
                  errors.nombre ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: Plan Básico, Plan Premium"
              />
              {errors.nombre && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.nombre}
                </p>
              )}
            </div>

            {/* Descripción Base */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline h-4 w-4 mr-1" />
                Descripción Base *
              </label>
              <textarea
                value={descripcionBase}
                onChange={handleDescripcionBaseChange}
                rows={3}
                className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm resize-none ${
                  errors.descripcion ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                placeholder="Descripción general del plan"
              />
              {errors.descripcion && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.descripcion}
                </p>
              )}
            </div>

            {/* Beneficios/Indicadores */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  <List className="inline h-4 w-4 mr-1" />
                  Beneficios e Indicadores
                </label>
                <button
                  type="button"
                  onClick={agregarBeneficio}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Agregar Beneficio
                </button>
              </div>
              <div className="space-y-3">
                {beneficios.map((beneficio, index) => (
                  <div key={index} className="flex gap-2">
                    <div className="flex-shrink-0 w-8 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-sm font-medium text-gray-600">
                      {index + 1}
                    </div>
                    <input
                      type="text"
                      value={beneficio}
                      onChange={(e) => handleBeneficioChange(index, e.target.value)}
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm"
                      placeholder={`Beneficio ${index + 1} (ej: Acceso a gimnasio, Clases grupales, etc.)`}
                    />
                    {beneficios.length > 1 && (
                      <button
                        type="button"
                        onClick={() => eliminarBeneficio(index)}
                        className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar beneficio"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

            </div>



            {/* Precio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="inline h-4 w-4 mr-1" />
                Precio (USD) *
              </label>
              <input
                type="number"
                name="monto"
                value={formData.monto}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm ${
                  errors.monto ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                placeholder="0.00"
              />
              {errors.monto && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.monto}
                </p>
              )}
            </div>

            {/* Horarios */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Horarios del Plan</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="inline h-4 w-4 mr-1" />
                    Hora de Inicio *
                  </label>
                  <input
                    type="time"
                    name="hora_inicio"
                    value={formData.hora_inicio}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm ${
                      errors.hora_inicio ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.hora_inicio && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.hora_inicio}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="inline h-4 w-4 mr-1" />
                    Hora de Fin *
                  </label>
                  <input
                    type="time"
                    name="hora_fin"
                    value={formData.hora_fin}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm ${
                      errors.hora_fin ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.hora_fin && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.hora_fin}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Estado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado
              </label>
              <select
                name="estado"
                value={formData.estado}
                onChange={(e) => setFormData(prev => ({ ...prev, estado: e.target.value === 'true' }))}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm"
              >
                <option value={true}>Activo</option>
                <option value={false}>Inactivo</option>
              </select>
            </div>

            {/* Botones */}
            <div className="flex gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/admin/centro/planes')}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {loading ? 'Guardando...' : 'Guardar Plan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CrearPlan;